import React, { useEffect } from 'react';
import { useCurrentUser } from 'store/user';
import { FormattedMessage, formatTranslation } from 'intl';
import { UserTranslationKeys, CommonTranslationKeys, ValidationTranslationKeys } from 'intl/keys';
import { useIntl } from 'react-intl';
import { ButtonContained } from 'components/general';
import { Box, TextField } from '@mui/material';
import useFormEdit from 'views/issue/forms/useFormEdit';
import { FieldSize } from 'shared/types/fieldSize';
import { useUserStore } from 'store/user';
import { showChannelRequestErrorToast } from 'services/toaster';

const LABEL_MAX_LENGTH = FieldSize.M;

export const SuperadminUser = (): React.ReactElement => {
  const formName = 'currentSuperadminUser';
  const user = useCurrentUser();
  const { setEditing } = useFormEdit();
  const intl = useIntl();
  const { updateSuperadminLabel } = useUserStore.use.actions();

  const [label, setLabel] = React.useState('');
  const [initialLabel, setInitialLabel] = React.useState('');
  const [labelError, setLabelError] = React.useState('');

  useEffect(() => {
    if (user.superadminLabel) {
      setLabel(user.superadminLabel);
      setInitialLabel(user.superadminLabel);
    }
  }, [user.superadminLabel]);

  const validateLabel = (value: string): string => {
    if (value.length > LABEL_MAX_LENGTH) {
      return formatTranslation(intl, ValidationTranslationKeys.stringLengthMaximumWeb, { maxLength: LABEL_MAX_LENGTH });
    }
    return '';
  };

  const handleLabelChange = (value: string): void => {
    const error = validateLabel(value);
    setLabelError(error);
    setLabel(value);
    setEditing(true, formName);
  };

  const hasModifications = (): boolean => {
    return label !== initialLabel;
  };

  const handleSave = async (): Promise<void> => {
    try {
      await updateSuperadminLabel(label);

      setEditing(false, formName);
      setInitialLabel(label);
    } catch (error) {
      showChannelRequestErrorToast('Failed to update superadmin data', error);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexFlow: 'column nowrap',
        gap: 2,
      }}
    >
      <TextField
        fullWidth
        variant='outlined'
        value={label}
        onChange={(e) => handleLabelChange(e.target.value)}
        data-qa='user-view-label-input'
        label={formatTranslation(intl, UserTranslationKeys.viewNameInputLabel)}
        error={!!labelError}
        helperText={labelError || `${label.length}/${LABEL_MAX_LENGTH}`}
        inputProps={{ maxLength: LABEL_MAX_LENGTH }}
      />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <ButtonContained
          onClick={handleSave}
          disabled={!hasModifications() || !!labelError}
          data-qa='user-view-save-button'
        >
          <FormattedMessage id={CommonTranslationKeys.save} />
        </ButtonContained>
      </Box>
    </Box>
  );
};
